import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Modal } from '@/components/modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { updateSkill } from '@/services/skills';
import type { Skill } from '@/services/skills';

interface EditSkillModalProps {
  skill: Skill;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditSkillModal = ({
  skill,
  open,
  onClose,
  onSuccess,
}: EditSkillModalProps) => {
  const [name, setName] = useState(skill.name);
  const [description, setDescription] = useState(skill.description);

  useEffect(() => {
    setName(skill.name);
    setDescription(skill.description);
  }, [skill]);

  const mutation = useMutation({
    mutationFn: updateSkill,
    onSuccess,
    retry: false,
  });

  const handleSubmit = () => {
    if (!name.trim()) return;
    mutation.mutate({
      skillId: skill.skill_id,
      name: name.trim(),
      description: description.trim(),
    });
  };

  const hasChanges =
    name.trim() !== skill.name || description.trim() !== skill.description;

  return (
    <Modal
      isDialogOpen={open}
      setIsDialogOpen={(v) => !v && onClose()}
      title='Edit Skill'
      contentClassName='sm:max-w-md'
      footer={
        <div className='flex justify-end gap-2'>
          <Button variant='ghost' onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || !hasChanges || mutation.isPending}
          >
            {mutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      }
    >
      <div className='space-y-4'>
        <div className='space-y-1.5'>
          <Label htmlFor='edit-skill-name'>
            Name<span className='text-destructive ml-0.5'>*</span>
          </Label>
          <Input
            id='edit-skill-name'
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className='space-y-1.5'>
          <Label htmlFor='edit-skill-desc'>Description</Label>
          <Textarea
            id='edit-skill-desc'
            placeholder='What does this skill do?'
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {mutation.isError && (
          <p className='text-sm text-destructive'>
            {mutation.error instanceof Error
              ? mutation.error.message
              : 'Failed to update skill. Please try again.'}
          </p>
        )}
      </div>
    </Modal>
  );
};
